/**
 * Debug Log Detail Page
 *
 * Full-screen view of a debug report with rendered markdown.
 */

import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { Router } from "@vaadin/router";
import { format } from "date-fns";
import { marked } from "marked";
import { debugService, type DebugReportDetail } from "../services/debug";
import { authService } from "../services/auth";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "../components/app-shell";

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

@customElement("debug-log-detail-page")
export class DebugLogDetailPage extends LitElement {
  @property({ type: String }) reportId = "";

  @state() private report: DebugReportDetail | null = null;
  @state() private isLoading = true;
  @state() private error = "";
  @state() private showDeleteDialog = false;
  @state() private isDeleting = false;

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 900px;
      margin: 0 auto;
    }

    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
    }

    .header-left h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.5rem;
      color: #333;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .header-actions {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .meta-card {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 0.75rem;
    }

    .meta-item {
      font-size: 0.875rem;
    }

    .meta-item label {
      font-weight: 600;
      color: #6c757d;
      display: block;
      margin-bottom: 0.25rem;
    }

    .user-note-card {
      background: #fff3cd;
      border-left: 4px solid #ffc107;
      padding: 1rem;
      margin-bottom: 1.5rem;
      border-radius: 0 0.5rem 0.5rem 0;
    }

    .user-note-card h3 {
      margin: 0 0 0.5rem 0;
      color: #856404;
      font-size: 1rem;
    }

    .user-note-card p {
      margin: 0;
      white-space: pre-wrap;
    }

    .markdown-section h3 {
      margin: 0 0 1rem 0;
      font-size: 1rem;
      color: #333;
    }

    .markdown-content {
      background: white;
      border: 1px solid #dee2e6;
      border-radius: 0.5rem;
      padding: 1.5rem;
      overflow-x: auto;
    }

    /* Markdown rendered styles */
    .markdown-content h1 {
      font-size: 1.5rem;
      border-bottom: 2px solid #dee2e6;
      padding-bottom: 0.5rem;
      margin: 1.5rem 0 1rem 0;
    }

    .markdown-content h1:first-child {
      margin-top: 0;
    }

    .markdown-content h2 {
      font-size: 1.25rem;
      border-bottom: 1px solid #dee2e6;
      padding-bottom: 0.25rem;
      margin: 1.25rem 0 0.75rem 0;
    }

    .markdown-content h3 {
      font-size: 1.1rem;
      margin: 1rem 0 0.5rem 0;
    }

    .markdown-content h4 {
      font-size: 1rem;
      margin: 0.75rem 0 0.5rem 0;
    }

    .markdown-content p {
      margin: 0.5rem 0;
      line-height: 1.6;
    }

    .markdown-content ul,
    .markdown-content ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    .markdown-content li {
      margin: 0.25rem 0;
    }

    .markdown-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
      font-size: 0.875rem;
    }

    .markdown-content th,
    .markdown-content td {
      border: 1px solid #dee2e6;
      padding: 0.5rem 0.75rem;
      text-align: left;
    }

    .markdown-content th {
      background: #f8f9fa;
      font-weight: 600;
    }

    .markdown-content tr:nth-child(even) {
      background: #f8f9fa;
    }

    .markdown-content code {
      background: #f4f4f4;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-family: monospace;
      font-size: 0.875em;
    }

    .markdown-content pre {
      background: #f4f4f4;
      padding: 1rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 1rem 0;
    }

    .markdown-content pre code {
      background: none;
      padding: 0;
    }

    .markdown-content hr {
      border: none;
      border-top: 1px solid #dee2e6;
      margin: 1.5rem 0;
    }

    .markdown-content strong {
      font-weight: 600;
    }

    .markdown-content blockquote {
      border-left: 4px solid #dee2e6;
      margin: 1rem 0;
      padding-left: 1rem;
      color: #6c757d;
    }

    .loading-state,
    .error-state {
      text-align: center;
      padding: 3rem 1rem;
    }

    .loading-state wa-spinner {
      font-size: 2rem;
    }

    .error-state wa-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    /* Delete dialog */
    .delete-dialog-content {
      text-align: center;
      padding: 1rem;
    }

    .delete-dialog-content wa-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    .delete-dialog-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: center;
      margin-top: 1.5rem;
    }

    @media (max-width: 600px) {
      .page-content {
        padding: 0.75rem;
      }

      .header {
        flex-direction: column;
      }

      .header-actions {
        width: 100%;
      }

      .header-actions wa-button {
        flex: 1;
      }

      .markdown-content {
        padding: 1rem;
      }

      .markdown-content table {
        font-size: 0.75rem;
      }

      .markdown-content th,
      .markdown-content td {
        padding: 0.375rem 0.5rem;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._loadReport();
  }

  // Vaadin Router callback
  onBeforeEnter(location: { params: { id?: string } }) {
    this.reportId = location.params.id || "";
  }

  private async _loadReport() {
    if (!this.reportId) {
      this.error = "No report ID provided";
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.error = "";

    try {
      this.report = await debugService.getReport(this.reportId);
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load report";
    } finally {
      this.isLoading = false;
    }
  }

  private async _handleDelete() {
    if (!this.report) return;

    this.isDeleting = true;

    try {
      await debugService.deleteReport(this.report.reportId);
      Router.go("/admin/debug-logs");
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to delete report";
      this.showDeleteDialog = false;
    } finally {
      this.isDeleting = false;
    }
  }

  private _renderMarkdown(markdown: string): unknown {
    try {
      const html = marked(markdown) as string;
      return unsafeHTML(html);
    } catch {
      return html`<pre>${markdown}</pre>`;
    }
  }

  render() {
    const auth = authService.state;
    if (!auth.isAuthenticated || !auth.isAdmin) {
      return html`
        <app-shell pageTitle="Debug Report">
          <wa-callout variant="danger" open>
            <wa-icon slot="icon" name="circle-exclamation"></wa-icon>
            Admin access required
          </wa-callout>
        </app-shell>
      `;
    }

    return html`
      <app-shell pageTitle="Debug Report">
        <div class="page-content">
          ${this.isLoading
            ? this._renderLoading()
            : this.error
              ? this._renderError()
              : this.report
                ? this._renderReport()
                : this._renderNotFound()}
        </div>
        ${this._renderDeleteDialog()}
      </app-shell>
    `;
  }

  private _renderLoading() {
    return html`
      <div class="loading-state">
        <wa-spinner></wa-spinner>
        <p>Loading report...</p>
      </div>
    `;
  }

  private _renderError() {
    return html`
      <div class="error-state">
        <wa-icon name="circle-exclamation"></wa-icon>
        <h3>Error Loading Report</h3>
        <p>${this.error}</p>
        <wa-button
          variant="neutral"
          @click=${() => Router.go("/admin/debug-logs")}
        >
          <wa-icon slot="prefix" name="arrow-left"></wa-icon>
          Back to Debug Logs
        </wa-button>
      </div>
    `;
  }

  private _renderNotFound() {
    return html`
      <div class="error-state">
        <wa-icon name="file-circle-question"></wa-icon>
        <h3>Report Not Found</h3>
        <p>The requested debug report could not be found.</p>
        <wa-button
          variant="neutral"
          @click=${() => Router.go("/admin/debug-logs")}
        >
          <wa-icon slot="prefix" name="arrow-left"></wa-icon>
          Back to Debug Logs
        </wa-button>
      </div>
    `;
  }

  private _renderReport() {
    const report = this.report!;
    return html`
      <div class="header">
        <div class="header-left">
          <h1>
            ${report.pageName}
            ${report.isResolved
              ? html`<wa-badge variant="neutral">Resolved</wa-badge>`
              : html`<wa-badge variant="warning">Open</wa-badge>`}
          </h1>
        </div>
        <div class="header-actions">
          <wa-button
            variant="neutral"
            @click=${() => Router.go("/admin/debug-logs")}
          >
            <wa-icon slot="prefix" name="arrow-left"></wa-icon>
            Back
          </wa-button>
          <wa-button
            variant="danger"
            @click=${() => (this.showDeleteDialog = true)}
          >
            <wa-icon slot="prefix" name="trash"></wa-icon>
            Delete
          </wa-button>
        </div>
      </div>

      <div class="meta-card">
        <div class="meta-grid">
          <div class="meta-item">
            <label>User</label>
            <div>${report.userDisplayName}</div>
            <div style="color: #6c757d; font-size: 0.75rem;">
              ${report.userEmail}
            </div>
          </div>
          <div class="meta-item">
            <label>Submitted</label>
            <div>
              ${format(new Date(report.createdAt), "MMMM d, yyyy 'at' HH:mm")}
            </div>
          </div>
          <div class="meta-item">
            <label>URL</label>
            <div style="word-break: break-all;">${report.url}</div>
          </div>
          <div class="meta-item">
            <label>Screen Size</label>
            <div>${report.screenWidth} × ${report.screenHeight}</div>
          </div>
          ${report.resolvedAt
            ? html`
                <div class="meta-item">
                  <label>Resolved</label>
                  <div>
                    ${format(
                      new Date(report.resolvedAt),
                      "MMMM d, yyyy 'at' HH:mm",
                    )}
                  </div>
                </div>
              `
            : ""}
        </div>
      </div>

      <div class="user-note-card">
        <h3>User's Issue Description</h3>
        <p>${report.userNote}</p>
      </div>

      <div class="markdown-section">
        <h3>Captured Page State</h3>
        <div class="markdown-content">
          ${this._renderMarkdown(report.markdown)}
        </div>
      </div>
    `;
  }

  private _renderDeleteDialog() {
    return html`
      <wa-dialog
        ?open=${this.showDeleteDialog}
        @wa-request-close=${() => (this.showDeleteDialog = false)}
        label="Confirm Delete"
      >
        <div class="delete-dialog-content">
          <wa-icon name="triangle-exclamation"></wa-icon>
          <h3>Delete Debug Report?</h3>
          <p>This action cannot be undone.</p>

          <div class="delete-dialog-actions">
            <wa-button
              variant="neutral"
              @click=${() => (this.showDeleteDialog = false)}
              ?disabled=${this.isDeleting}
            >
              Cancel
            </wa-button>
            <wa-button
              variant="danger"
              @click=${this._handleDelete}
              ?loading=${this.isDeleting}
            >
              Delete
            </wa-button>
          </div>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "debug-log-detail-page": DebugLogDetailPage;
  }
}
